import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AiSuggestionsResponse, AiConfidence } from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { Spinner } from "./ui.js";

const CONF: Record<AiConfidence, { label: string; cls: string }> = {
  alta: { label: "Confiança alta", cls: "bg-green-100 text-green-700" },
  media: { label: "Confiança média", cls: "bg-amber-100 text-amber-800" },
  baixa: { label: "Confiança baixa", cls: "bg-slate-100 text-slate-600" },
};

// Sugestões da IA (narrativa gerada por LLM sobre agregados). Distinta dos
// "avisos automáticos" (regras). Botão de regenerar ignora o cache do servidor.
export function SugestoesIA() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(true);
  const q = useQuery({
    queryKey: ["ai-sugestoes"],
    queryFn: async () => (await api.get("/insights/ai-sugestoes")).data as AiSuggestionsResponse,
  });
  const data = q.data;

  const regenerate = async () => {
    await api.get("/insights/ai-sugestoes?refresh=1");
    qc.invalidateQueries({ queryKey: ["ai-sugestoes"] });
  };

  return (
    <div className="card border-brand-100 p-5" data-testid="sugestoes-ia">
      <button className="flex w-full items-center justify-between" onClick={() => setOpen((v) => !v)}>
        <div className="flex items-center gap-2">
          <span className="text-lg">🤖</span>
          <h2 className="font-semibold text-slate-800">Sugestões da IA</h2>
          {data && (
            <span className={`badge ${data.source === "ia" ? "bg-brand-100 text-brand-700" : "bg-slate-100 text-slate-500"}`}>
              {data.source === "ia" ? `Gerado por IA · ${data.model}` : "Síntese heurística"}
            </span>
          )}
        </div>
        <span className="text-slate-400">{open ? "▾" : "▸"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-2">
          {q.isLoading && <Spinner label="Consultando a IA…" />}
          {data && (
            <>
              {data.items.map((s, i) => (
                <div key={s.id} data-testid="sugestao-ia" className="flex gap-3 rounded-lg border border-slate-100 p-3 hover:bg-slate-50">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-bold text-brand-600">{i + 1}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-800">{s.title}</span>
                      <span className={`badge ${CONF[s.confidence].cls}`}>{CONF[s.confidence].label}</span>
                    </div>
                    {s.rationale && <p className="mt-1 text-sm text-slate-500">{s.rationale}</p>}
                    <p className="mt-1 text-sm text-brand-700">→ {s.recommendation}</p>
                    {s.basedOn.length > 0 && (
                      <p className="mt-1 text-xs text-slate-400">Baseado em: {s.basedOn.join(" · ")}</p>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-slate-400">⚠ {data.disclaimer}</p>
                <button className="btn-ghost text-xs" onClick={regenerate}>↻ Regenerar</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
